import React, {useRef} from 'react';

import {ProcessesFeature, V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import UniversalBlockGlobalClientStates from 'insights/universal-blocks/UniversalBlockGlobalClientStates';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {convertRunQueryStateToLegacyState} from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import {useWritableContext} from 'insights/WritableContext';
import useSelectorsById, {selectorsAreLoaded} from 'notebook/structured-tables/hooks/useSelectorsById';
import {useProcessTaskSchemasForCreateWithLoading} from 'processes/CreateProcessUtil';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import Menu from 'ui-library/menus/Menu';
import Section from 'ui-library/menus/Section';

import BaseBlockOverflowDropdown from './BaseBlockOverflowDropdown';
import {SHARED_MENU_PROPS} from './BlockOverflowMenuConstants';
import getAddToWorklistOption from './getAddToWorklistOption';
import getCloneBlockOption from './getCloneBlockOption';
import getCopyAsTSVOption from './getCopyAsTSVOption';
import getCreateProcessTasksOption from './getCreateProcessTasksOption';
import getCreateRequestV2SubmissionOption, {
  useRequestV2DefinitionsForCreateDropdownWithLoading,
} from './getCreateRequestV2SubmissionOption';
import getDeleteBlockOption from './getDeleteBlockOption';
import getDownloadAsCSVOption, {ExportBlockFn} from './getDownloadAsCSVOption';
import getExecuteProcessTasksOption from './getExecuteProcessTasksOption';
import getExecuteRequestOption from './getExecuteRequestOption';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

interface Actions {
  cloneBlock: () => void;
  deleteBlock: () => void;
  exportBlock: ExportBlockFn;
}

/** @package */
export interface AnalyticsBlockOverflowDropdownProps {
  actions: Actions;
  blockViewStateId: string;
  className?: string;
  dashboardAttrs: Pick<AnalyticsDashboard, 'analyticsQueries'>;
  runQueryState: RunQueryState;
}

/**
 * Overflow menu for dashboard editor's block
 *
 * Options to download CSV, copy TSV, add to worklist, duplicate block, and delete block
 */
export default function AnalyticsBlockOverflowDropdown({
  actions,
  blockViewStateId,
  className,
  dashboardAttrs,
  runQueryState,
}: AnalyticsBlockOverflowDropdownProps): JSX.Element {
  const processTaskSchemas = useProcessTaskSchemasForCreateWithLoading();
  const requestV2Definitions = useRequestV2DefinitionsForCreateDropdownWithLoading();
  const selectorsById = useSelectorsById({includeArchivedSelectors: true});
  const [sortState] = UniversalBlockGlobalClientStates.sortState.useGlobalClientState(blockViewStateId);
  const writable = useWritableContext();
  const tsvTextRef = useRef<string | null>('');

  const optionProps = {
    actions,
    runQueryState: convertRunQueryStateToLegacyState(runQueryState),
    showRawOutput: false,
    sortState,
    tsvTextRef,
  };

  const totalRows =
    runQueryState.status === AsyncStatus.SUCCESS ? runQueryState.data.dataFrame.rows.length : 0;

  const validForTaskGroup = totalRows > 0 && totalRows < BenchlingConfig.WORKFLOWS_MAX_TASKS_PER_TASK_GROUP;

  const canCreateWorkflows = validForTaskGroup && selectorsAreLoaded(selectorsById);

  return (
    <BaseBlockOverflowDropdown
      className={className}
      menu={
        <Menu {...SHARED_MENU_PROPS}>
          <Section>
            {getDownloadAsCSVOption(optionProps)}
            {getCopyAsTSVOption(optionProps)}
            {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS ? getAddToWorklistOption(optionProps) : null}
            {BenchlingConfig.ENABLE_REQUESTS ? getExecuteRequestOption(optionProps) : null}
            {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) && canCreateWorkflows
              ? getCreateProcessTasksOption({runQueryState, processTaskSchemas, selectorsById})
              : null}
            {isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_CREATE}) && canCreateWorkflows
              ? getCreateRequestV2SubmissionOption({runQueryState, requestV2Definitions, selectorsById})
              : null}
            {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_EDIT}) && validForTaskGroup
              ? getExecuteProcessTasksOption(optionProps)
              : null}
          </Section>
          {writable && (
            <Section>
              {getCloneBlockOption({...optionProps, dashboardAttrs})}
              {getDeleteBlockOption(optionProps)}
            </Section>
          )}
        </Menu>
      }
    />
  );
}
